import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

/**
 * Service to manage error states in the application.
 * Provides methods to observe, set, and clear errors associated with specific keys.
 */
@Injectable({ providedIn: 'root' })
export class ErrorService {
  /**
   * Stores error states as a map of keys to BehaviorSubjects.
   * Each key represents a specific operation or context, and its value is the current error message or `null`.
   */
  private errorStates = new Map<string, BehaviorSubject<string | null>>();

  /**
   * Returns an observable for the error state of a given key.
   * If the key does not exist in the map, it initializes a new entry with a default value of `null`.
   *
   * @param key - A unique identifier for the operation or context.
   * @returns Observable that emits the error message or `null`.
   */
  getError$(key: string) {
    if (!this.errorStates.has(key)) {
      // If the key doesn't exist, create a new entry with a default value of `null`.
      this.errorStates.set(key, new BehaviorSubject<string | null>(null));
    }
    // Return the observable for the key's error state.
    return this.errorStates.get(key)!.asObservable();
  }

  /**
   * Sets an error message for a given key.
   * If the key does not exist in the map, it initializes a new entry.
   *
   * @param key - A unique identifier for the operation or context.
   * @param message - The error message to associate with the key.
   */
  setError(key: string, message: string): void {
    if (!this.errorStates.has(key)) {
      // Initialize the error state for the key if it doesn't exist.
      this.errorStates.set(key, new BehaviorSubject<string | null>(null));
    }
    // Update the error message for the key.
    this.errorStates.get(key)!.next(message);
  }

  /**
   * Clears the error message for a given key by setting it to `null`.
   * If the key does not exist, no action is taken.
   *
   * @param key - A unique identifier for the operation or context.
   */
  clearError(key: string): void {
    if (this.errorStates.has(key)) {
      // Reset the error message for the key to `null`.
      this.errorStates.get(key)!.next(null);
    }
  }
}
